import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Server, Database, Globe, Terminal, Zap, RefreshCw } from "lucide-react";
import { config } from "@/lib/config";
import { supabase } from "@/integrations/supabase/client";

type ServiceStatus = "checking" | "operational" | "degraded" | "down";

interface ServiceCheck {
  name: string;
  status: ServiceStatus;
  latency?: number;
  detail?: string;
  icon: React.ElementType;
}

const statusConfig: Record<ServiceStatus, { label: string; color: string; Icon: React.ElementType }> = {
  checking: { label: "Checking…", color: "text-muted-foreground", Icon: Loader2 },
  operational: { label: "Operational", color: "text-green-500", Icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "text-yellow-500", Icon: AlertTriangle },
  down: { label: "Down", color: "text-destructive", Icon: XCircle },
};

function StatusIndicator({ status }: { status: ServiceStatus }) {
  const { label, color, Icon } = statusConfig[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${color}`}>
      <Icon className={`h-4 w-4 ${status === "checking" ? "animate-spin" : ""}`} />
      {label}
    </span>
  );
}

export default function DebugOverview() {
  const [services, setServices] = useState<ServiceCheck[]>([
    { name: "Backend API", status: "checking", icon: Server },
    { name: "Database", status: "checking", icon: Database },
    { name: "Uptime Monitor", status: "checking", icon: Globe },
  ]);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const runChecks = async () => {
    setRefreshing(true);
    setServices((s) => s.map((svc) => ({ ...svc, status: "checking" as ServiceStatus })));

    const results: ServiceCheck[] = [];

    // 1. Backend API health
    const backendStart = performance.now();
    try {
      const res = await fetch(`${config.backendUrl}/api/health`, { signal: AbortSignal.timeout(8000) });
      const latency = Math.round(performance.now() - backendStart);
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        results.push({ name: "Backend API", status: "operational", latency, detail: data.version ? `v${data.version}` : undefined, icon: Server });
      } else {
        results.push({ name: "Backend API", status: "degraded", latency, detail: `HTTP ${res.status}`, icon: Server });
      }
    } catch {
      results.push({ name: "Backend API", status: "down", latency: Math.round(performance.now() - backendStart), detail: "Unreachable", icon: Server });
    }

    // 2. Database (Supabase) connectivity
    const dbStart = performance.now();
    try {
      const { error } = await supabase.from("profiles").select("id").limit(1);
      const latency = Math.round(performance.now() - dbStart);
      results.push({ name: "Database", status: error ? "degraded" : "operational", latency, detail: error?.message, icon: Database });
    } catch {
      results.push({ name: "Database", status: "down", latency: Math.round(performance.now() - dbStart), detail: "Unreachable", icon: Database });
    }

    // 3. Uptime monitor edge function
    const uptimeStart = performance.now();
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/uptime-status`, {
        headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        signal: AbortSignal.timeout(8000),
      });
      const latency = Math.round(performance.now() - uptimeStart);
      if (res.ok) {
        const data = await res.json();
        results.push({ name: "Uptime Monitor", status: data.status === "operational" ? "operational" : "degraded", latency, detail: data.label, icon: Globe });
      } else {
        results.push({ name: "Uptime Monitor", status: "degraded", latency, detail: `HTTP ${res.status}`, icon: Globe });
      }
    } catch {
      results.push({ name: "Uptime Monitor", status: "down", latency: Math.round(performance.now() - uptimeStart), detail: "Unreachable", icon: Globe });
    }

    setServices(results);
    setLastChecked(new Date());
    setRefreshing(false);
  };

  useEffect(() => { runChecks(); }, []);

  const overallStatus: ServiceStatus = services.some((s) => s.status === "down")
    ? "down"
    : services.some((s) => s.status === "degraded")
    ? "degraded"
    : services.every((s) => s.status === "operational")
    ? "operational"
    : "checking";

  return (
    <div className="space-y-6">
      {/* Overall status */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">System Status</CardTitle>
          <div className="flex items-center gap-3">
            {lastChecked && (
              <span className="text-xs text-muted-foreground">
                Last checked {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={runChecks} disabled={refreshing}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-6">
            <StatusIndicator status={overallStatus} />
            <span className="text-sm text-muted-foreground">
              {overallStatus === "operational" && "All services are running normally."}
              {overallStatus === "degraded" && "Some services are experiencing issues."}
              {overallStatus === "down" && "One or more services are unreachable."}
              {overallStatus === "checking" && "Running health checks…"}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {services.map((svc) => {
              const SvcIcon = svc.icon;
              return (
                <div key={svc.name} className="flex items-start gap-3 rounded-lg border border-border p-4">
                  <SvcIcon className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{svc.name}</p>
                    <StatusIndicator status={svc.status} />
                    <div className="flex items-center gap-2 mt-1">
                      {svc.latency != null && (
                        <Badge variant="secondary" className="text-xs">{svc.latency}ms</Badge>
                      )}
                      {svc.detail && (
                        <span className="text-xs text-muted-foreground truncate">{svc.detail}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick links */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Link to="/admin/debug/backend">
          <Card className="hover:border-primary/40 transition-colors cursor-pointer">
            <CardContent className="flex items-center gap-4 p-5">
              <Terminal className="h-8 w-8 text-primary shrink-0" />
              <div>
                <p className="font-medium">Backend Health</p>
                <p className="text-sm text-muted-foreground">API health checks, system info, and run management</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/debug/execution">
          <Card className="hover:border-primary/40 transition-colors cursor-pointer">
            <CardContent className="flex items-center gap-4 p-5">
              <Zap className="h-8 w-8 text-primary shrink-0" />
              <div>
                <p className="font-medium">Execution Flow</p>
                <p className="text-sm text-muted-foreground">Test run execution, monitor progress and events</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
